// Story 7.12 Iteration 2 (ADO-911), DR-3 — the PDP's collapsible section.
//
// The PO-approved PDP (Figma 1169:7024 expanded / 1193:7661 folded) stacks three
// INDEPENDENTLY collapsible panels — Product Information, Stock Card,
// Nutritional Information — each a white card with a title row and a caret that
// flips between states. There is no disclosure primitive in the design system
// (the closest, CollapsibleFilterSidebar, is a layout rail, not a panel), so this
// is a small local component rather than a DS addition: it is PDP chrome, and
// promoting it to `design-system/` is a follow-up once a second surface needs it.
//
// a11y (NFR24–26): a real <button> header carrying `aria-expanded` +
// `aria-controls`, and a region labelled by that header. The panel is unmounted
// when collapsed, so `aria-controls` is only emitted while the panel exists (the
// dangling-IDREF lesson from the Iteration-1 code review).

import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../../design-system';
import styles from './Accordion.module.css';

export interface AccordionProps {
  readonly title: string;
  /** Initial state. Defaults to CLOSED — the PO-approved design ships two states
   *  (folded `1193:7661` / expanded `1169:7024`) and folded is the initial one:
   *  it keeps the hero and the "Often purchased together" carousel above the
   *  fold, and every panel is one click away. */
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}

export function Accordion({ title, defaultOpen = false, children }: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const headerId = useId();

  return (
    <section className={styles.accordion}>
      <h2 className={styles.heading}>
        <button
          type="button"
          id={headerId}
          className={styles.trigger}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={styles.title}>{title}</span>
          <span className={styles.caret} data-open={open} aria-hidden="true">
            <Icon name="caretDown" size={20} />
          </span>
        </button>
      </h2>
      {open && (
        <div id={panelId} className={styles.panel} role="region" aria-labelledby={headerId}>
          {children}
        </div>
      )}
    </section>
  );
}
